import { graphql, readInlineData } from "react-relay";

import { article as articleData, paywall } from "shared/data";
import { notEmpty } from "shared/lib/utils";

import { getArticleType } from "scmp-app/components/article/article-render/helpers";
import { checkIsPlusArticle } from "scmp-app/components/article/article-render/plus/helpers";
import type { helpersIsArchiveWallArticle$key } from "scmp-app/queries/__generated__/helpersIsArchiveWallArticle.graphql";
import type { helpersIsLiteWallArticle$key } from "scmp-app/queries/__generated__/helpersIsLiteWallArticle.graphql";
import type { helpersIsPlusInlinePaywall$key } from "scmp-app/queries/__generated__/helpersIsPlusInlinePaywall.graphql";
import type { helpersIsSignInWallArticle$key } from "scmp-app/queries/__generated__/helpersIsSignInWallArticle.graphql";
import type { helpersIsVelocityWallArticle$key } from "scmp-app/queries/__generated__/helpersIsVelocityWallArticle.graphql";
export const checkArticleIsInLiteUpgradeWall = (
  article_: helpersIsLiteWallArticle$key,
  isLiteUser: boolean,
  liteArticleTimeFrame: number,
) => {
  const article = readInlineData(
    graphql`
      fragment helpersIsLiteWallArticle on Article @inline {
        createdDate
        publishedDate
      }
    `,
    article_,
  );

  const isLiteArticleDate = Date.now() - 1000 * 60 * 60 * 24 * liteArticleTimeFrame;
  const articleDate = notEmpty(article.publishedDate) ? article.publishedDate : article.createdDate;

  return isLiteUser && !!articleDate && isLiteArticleDate > articleDate;
};

export const checkArticleIsInArchiveWall = (
  article_: helpersIsArchiveWallArticle$key,
  archiveArticleTimeFrame: number,
) => {
  const article = readInlineData(
    graphql`
      fragment helpersIsArchiveWallArticle on Article @inline {
        ...helpersIsVelocityWallArticle
        createdDate
        publishedDate
        paywallTypes {
          entityId
        }
      }
    `,
    article_,
  );
  const isArchivedArticleDate = Date.now() - 1000 * 60 * 60 * 24 * archiveArticleTimeFrame;
  const articleDate = notEmpty(article.publishedDate) ? article.publishedDate : article.createdDate;
  // If either date is empty, it should not in archive wall
  if (!notEmpty(isArchivedArticleDate) || !notEmpty(articleDate)) return false;

  const paywallTypesHasNotArchived = notEmpty(
    article.paywallTypes?.find?.(
      paywallType => paywallType?.entityId === paywall.notArchive.entityId,
    ),
  );

  const isInVelocityWall = checkArticleIsInVelocityWall(article);
  return !paywallTypesHasNotArchived && !isInVelocityWall && isArchivedArticleDate > articleDate;
};

export const checkArticleIsInVelocityWall = (article_: helpersIsVelocityWallArticle$key) => {
  const article = readInlineData(
    graphql`
      fragment helpersIsVelocityWallArticle on Article @inline {
        paywallTypes {
          entityId
        }
      }
    `,
    article_,
  );
  const paywallTypesHasNotVelocity = notEmpty(
    article.paywallTypes?.find?.(
      paywallType => paywallType?.entityId === paywall.notVelocity.entityId,
    ),
  );

  const paywallTypesHasVelocity = notEmpty(
    article.paywallTypes?.find?.(
      paywallType => paywallType?.entityId === paywall.velocity.entityId,
    ),
  );

  return !paywallTypesHasNotVelocity && paywallTypesHasVelocity;
};

export const checkArticleIsInSignInWall = (article_: helpersIsSignInWallArticle$key) => {
  const article = readInlineData(
    graphql`
      fragment helpersIsSignInWallArticle on Article @inline {
        paywallTypes {
          entityId
        }
      }
    `,
    article_,
  );

  const paywallTypesHasSignIn = notEmpty(
    article.paywallTypes?.find?.(paywallType => paywallType?.entityId === paywall.signIn.entityId),
  );

  return paywallTypesHasSignIn;
};

export const checkArticleIsPlusInlinePaywall = (article_: helpersIsPlusInlinePaywall$key) => {
  const article = readInlineData(
    graphql`
      fragment helpersIsPlusInlinePaywall on Content @inline {
        ...helpersGetArticleTypeArticle
        ...helpersCheckIsPlusArticle
      }
    `,
    article_,
  );

  const isPlusArticle = checkIsPlusArticle(article);
  const articleTypeByEntityId = getArticleType(article);

  return (
    isPlusArticle && articleTypeByEntityId === articleData.types.chinaFutureTechBriefing.entityId
  );
};
